// Lesson Progress Tracker Service
// Theo dõi chi tiết tiến độ học bài theo 4 phần: Tương tác, Video, Trắc nghiệm, Mô phỏng

import { LessonProgressBreakdown } from '../../types/learningHistory';

class LessonProgressTrackerClass {
  private trackers: Map<string, LessonProgressBreakdown> = new Map();

  /**
   * Initialize tracker for a lesson
   */
  initializeLesson(
    lessonId: string,
    counts: {
      interactiveQuestions: number;
      videos: number;
      quizzes: number;
      simulations: number;
    }
  ): void {
    const breakdown: LessonProgressBreakdown = {
      interactive: {
        completed: false,
        progress: 0,
        questionsAnswered: 0,
        totalQuestions: counts.interactiveQuestions,
        timeSpent: 0
      },
      videos: {
        completed: false,
        progress: 0,
        videosWatched: 0,
        totalVideos: counts.videos,
        watchTimePercentage: 0
      },
      quiz: {
        completed: false,
        progress: 0,
        quizzesTaken: 0,
        totalQuizzes: counts.quizzes,
        timeSpent: 0
      },
      simulations: {
        completed: false,
        progress: 0,
        simulationsCompleted: 0,
        totalSimulations: counts.simulations,
        watchTimePercentage: 0
      },
      totalProgress: 0,
      totalTimeSpent: 0
    };

    this.trackers.set(lessonId, breakdown);
    console.log(`📊 [LessonProgressTracker] Initialized for ${lessonId}:`, counts);
  }

  /**
   * Update interactive progress
   */
  updateInteractive(lessonId: string, questionsAnswered: number, timeSpent: number): void {
    const tracker = this.trackers.get(lessonId);
    if (!tracker) return;

    tracker.interactive.questionsAnswered = questionsAnswered;
    tracker.interactive.timeSpent = timeSpent;

    if (tracker.interactive.totalQuestions > 0) {
      const percentage = Math.min(
        (questionsAnswered / tracker.interactive.totalQuestions) * 100,
        100
      );
      tracker.interactive.progress = (percentage / 100) * 25; // Max 25%
      tracker.interactive.completed = percentage >= 100;
    } else {
      // No interactive questions → auto complete this section
      tracker.interactive.progress = 25;
      tracker.interactive.completed = true;
    }

    this.recalculateTotal(lessonId);
    console.log(`📊 Interactive: ${tracker.interactive.progress.toFixed(1)}% (${questionsAnswered}/${tracker.interactive.totalQuestions})`);
  }

  /**
   * Update video progress
   * @param watchPercentage - Average watch percentage across all videos (0-100)
   */
  updateVideos(lessonId: string, videosWatched: number, watchPercentage: number): void {
    const tracker = this.trackers.get(lessonId);
    if (!tracker) return;

    tracker.videos.videosWatched = videosWatched;
    tracker.videos.watchTimePercentage = watchPercentage;

    if (tracker.videos.totalVideos > 0) {
      // If all videos watched to 100% → 25%
      // Otherwise, calculate based on average watch percentage
      if (videosWatched >= tracker.videos.totalVideos && watchPercentage >= 95) {
        tracker.videos.progress = 25;
        tracker.videos.completed = true;
      } else {
        // Progress = (average watch %) * 25%
        tracker.videos.progress = (watchPercentage / 100) * 25;
        tracker.videos.completed = false;
      }
    } else {
      // No videos → auto complete this section
      tracker.videos.progress = 25;
      tracker.videos.completed = true;
    }

    this.recalculateTotal(lessonId);
    console.log(`📊 Videos: ${tracker.videos.progress.toFixed(1)}% (${videosWatched}/${tracker.videos.totalVideos}, watch: ${watchPercentage.toFixed(1)}%)`);
  }

  /**
   * Update quiz progress
   */
  updateQuiz(lessonId: string, quizzesTaken: number, timeSpent: number): void {
    const tracker = this.trackers.get(lessonId);
    if (!tracker) return;

    tracker.quiz.quizzesTaken = quizzesTaken;
    tracker.quiz.timeSpent = timeSpent;

    if (tracker.quiz.totalQuizzes > 0) {
      const percentage = Math.min(
        (quizzesTaken / tracker.quiz.totalQuizzes) * 100,
        100
      );
      tracker.quiz.progress = (percentage / 100) * 25; // Max 25%
      tracker.quiz.completed = percentage >= 100;
    } else {
      // No quizzes → auto complete this section
      tracker.quiz.progress = 25;
      tracker.quiz.completed = true;
    }

    this.recalculateTotal(lessonId);
    console.log(`📊 Quiz: ${tracker.quiz.progress.toFixed(1)}% (${quizzesTaken}/${tracker.quiz.totalQuizzes})`);
  }

  /**
   * Update simulation progress
   * @param watchPercentage - Average watch percentage across all simulation videos (0-100)
   */
  updateSimulations(lessonId: string, simulationsCompleted: number, watchPercentage: number): void {
    const tracker = this.trackers.get(lessonId);
    if (!tracker) return;

    tracker.simulations.simulationsCompleted = simulationsCompleted;
    tracker.simulations.watchTimePercentage = watchPercentage;

    if (tracker.simulations.totalSimulations > 0) {
      // If all simulations watched to 100% → 25%
      // Otherwise, calculate based on average watch percentage
      if (simulationsCompleted >= tracker.simulations.totalSimulations && watchPercentage >= 95) {
        tracker.simulations.progress = 25;
        tracker.simulations.completed = true;
      } else {
        // Progress = (average watch %) * 25%
        tracker.simulations.progress = (watchPercentage / 100) * 25;
        tracker.simulations.completed = false;
      }
    } else {
      // No simulations → auto complete this section
      tracker.simulations.progress = 25;
      tracker.simulations.completed = true;
    }

    this.recalculateTotal(lessonId);
    console.log(`📊 Simulations: ${tracker.simulations.progress.toFixed(1)}% (${simulationsCompleted}/${tracker.simulations.totalSimulations}, watch: ${watchPercentage.toFixed(1)}%)`);
  }

  /**
   * Recalculate total progress and time spent
   */
  private recalculateTotal(lessonId: string): void {
    const tracker = this.trackers.get(lessonId);
    if (!tracker) return;

    // Total progress = sum of 4 components (each max 25%)
    tracker.totalProgress = Math.min(
      tracker.interactive.progress +
      tracker.videos.progress +
      tracker.quiz.progress +
      tracker.simulations.progress,
      100
    );

    // Total time = interactive + quiz ONLY (NO video/simulation)
    tracker.totalTimeSpent = tracker.interactive.timeSpent + tracker.quiz.timeSpent;

    console.log(`📊 [LessonProgressTracker] Total: ${tracker.totalProgress.toFixed(1)}%, Time: ${tracker.totalTimeSpent}s`);
  }

  /**
   * Get current breakdown for a lesson
   */
  getBreakdown(lessonId: string): LessonProgressBreakdown | null {
    return this.trackers.get(lessonId) || null;
  }

  /**
   * Get total progress percentage
   */
  getTotalProgress(lessonId: string): number {
    const tracker = this.trackers.get(lessonId);
    return tracker ? tracker.totalProgress : 0;
  }

  /**
   * Get total time spent (interactive + quiz only)
   */
  getTotalTimeSpent(lessonId: string): number {
    const tracker = this.trackers.get(lessonId);
    return tracker ? tracker.totalTimeSpent : 0;
  }

  /**
   * Clear tracker for a lesson
   */
  clearLesson(lessonId: string): void {
    this.trackers.delete(lessonId);
    console.log(`🗑️ [LessonProgressTracker] Cleared tracker for ${lessonId}`);
  }
}

// Export singleton instance
export const lessonProgressTracker = new LessonProgressTrackerClass();

